import { Toast } from '@gafe/common-ui';
import { AxiosError } from 'axios';

import { ActivityController, InfoToFetchActivityData } from '@/controllers/Activity';
import { LeadController, NewLeadFormData } from '@/controllers/Lead';
import { GoogleAnalytics } from '@/services/Analytics';
import { Sentry } from '@/services/Sentry';
import { RootDispatch } from '@/store';
import { fetchActivityDetailsData } from '@/store/reducers/activities/actions';
import { goToRepDashboard, leadCreationFormToRepInfo } from '@/store/reducers/leads/helpers';
import {
  AssignSalesRepresentativeVm,
  CustomErrorsMessages,
  SelfDisqualifyLeadVm,
  SelfQualifyLeadVm,
} from '@/types';
import { ValidationErrorResponse } from '@/types/api';

async function createNewLead(this: RootDispatch, formData: NewLeadFormData): Promise<void> {
  try {
    await LeadController.createNewLead(formData);
    const salesRepInfo = leadCreationFormToRepInfo(formData);

    goToRepDashboard(salesRepInfo);
    GoogleAnalytics.createNewLead();
    Toast({
      showIcon: true,
      type: 'success',
      message: "We'll get back to you within 48 hours.",
      title: `${formData.addressLine1} Submitted`,
    });
  } catch (e) {
    const { response: errorResponse } = e as AxiosError<ValidationErrorResponse>;
    const error = errorResponse?.data;

    if (errorResponse?.status === 400 && error) {
      const hasValidationsErrors = error?.validationErrors?.length > 0;

      const message = hasValidationsErrors
        ? error.validationErrors[0].error
        : CustomErrorsMessages.ValidationError;

      this.leads.setLeadCreationError(message);

      return;
    }

    // unknown type of error occurred
    this.leads.setLeadCreationError(CustomErrorsMessages.SomethingWentWrong);
    Sentry.captureError(e as Error);
  } finally {
    this.leads.setModalOpen(false);
  }
}

async function checkNewLead(this: RootDispatch, formData: NewLeadFormData): Promise<void> {
  try {
    const response = await ActivityController.fetchAllActivities({
      searchQuery: formData.addressLine1,
    });

    if (response.data.totalCount > 0) {
      this.leads.setFormData(formData);
      this.leads.setLeadDuplicateInfo(response.data);

      return;
    }
    await this.leads.createNewLead(formData);
  } catch (e) {
    this.leads.setLeadCreationError(CustomErrorsMessages.SomethingWentWrong);
    Sentry.captureError(e as Error);
  }
}

async function assignRepresentative(
  this: RootDispatch,
  payload: AssignSalesRepresentativeVm,
): Promise<void> {
  this.leads.setAssignmentInProgress(true);
  try {
    await LeadController.assignSalesRepresentative(payload);
  } catch (e) {
    Sentry.captureError(e as Error);
  } finally {
    this.leads.setAssignmentInProgress(false);
  }
}

async function qualifyLead(this: RootDispatch, payload: SelfQualifyLeadVm): Promise<void> {
  this.leads.setQualifyLeadInProgress(true);
  try {
    await LeadController.qualify(payload);
  } catch (e) {
    const errorBody = e as AxiosError<ValidationErrorResponse>;
    const error = errorBody.response?.data;

    Sentry.captureError(e as Error);
    this.leads.setQualifyingLeadError(error?.message ?? CustomErrorsMessages.SomethingWentWrong);
  } finally {
    this.leads.setQualifyLeadInProgress(false);
  }
}

async function disqualifyLead(
  this: RootDispatch,
  payload: { selfDisqualifyLead: SelfDisqualifyLeadVm; activityDetails: InfoToFetchActivityData },
): Promise<void> {
  this.leads.setDisqualifyLeadInProgress(true);
  try {
    const { selfDisqualifyLead, activityDetails } = payload;

    await LeadController.disqualify(selfDisqualifyLead);

    fetchActivityDetailsData(activityDetails);
  } catch (e) {
    const errorBody = e as AxiosError<ValidationErrorResponse>;
    const error = errorBody.response?.data;

    Sentry.captureError(e as Error);
    this.leads.setDisqualifyLeadError(error?.message ?? CustomErrorsMessages.SomethingWentWrong);
  } finally {
    this.leads.setDisqualifyLeadInProgress(false);
  }
}

export const leadEffect = (dispatch: RootDispatch) => ({
  createNewLead: createNewLead.bind(dispatch),
  checkNewLead: checkNewLead.bind(dispatch),
  assignRepresentative: assignRepresentative.bind(dispatch),
  qualifyLead: qualifyLead.bind(dispatch),
  disqualifyLead: disqualifyLead.bind(dispatch),
});
